import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import {
	type ContentAssignmentDTO,
	type ContentProductTextsDTO,
	type ContentResolutionDTO,
	type ContentResolvedItemDTO,
	ContentScopeType,
} from '@today/types'
import type {
	AssignmentListViewProps,
	AssignmentRowView,
	AttachDialogViewProps,
	LocaleChipView,
	ProductLegalTextsViewProps,
	ResolvedItemView,
	ResolvePreviewViewProps,
} from '@today/ui-domain'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { contentService } from '@/services/content.service'
import { textWarnings } from './assignment-rules'
import { contentErrorCode, day, serverMessage } from './content-ui'
import { inForceChips } from './text-list-rules'
import { useAssignmentList, useAttachDialog } from './useAssignments'
import {
	contentKeys,
	useContentLocales,
	useContentT,
	useContentTypes,
	useContentViewer,
	useNow,
} from './useContentQueries'

const toItem = (item: ContentResolvedItemDTO): ResolvedItemView => ({
	id: item.localizationId,
	code: item.code,
	title: item.title,
	body: item.body,
	versionNumber: item.versionNumber,
	locale: item.locale,
})

/** The product preview through `POST /texts/resolve` (TO-420 content-admin-ui REQ-011). */
export function useResolvePreview(productId: string): ResolvePreviewViewProps {
	const { t, errorMessage } = useContentT()
	const { locales } = useContentLocales()
	const [locale, setLocale] = useState('')
	const selected = locale || locales[0] || ''
	const resolve = useMutation({
		mutationFn: (): Promise<ContentResolutionDTO> =>
			contentService.resolve({ product_ids: [productId], locale: selected }),
	})
	const missing = contentErrorCode(resolve.error) === 'TEXT_NOT_AVAILABLE'
	return {
		t,
		locale: selected,
		locales,
		result: resolve.data
			? {
					withProduct: (resolve.data.byProduct[productId] ?? []).map(toItem),
					pageEnd: resolve.data.pageEnd.map(toItem),
				}
			: null,
		missing: missing ? serverMessage(resolve.error).replace(/^TEXT_NOT_AVAILABLE:\s*/, '') : null,
		error: resolve.error && !missing ? errorMessage(resolve.error) : null,
		isBusy: resolve.isPending,
		onLocaleChange: (next) => {
			setLocale(next)
			resolve.reset()
		},
		onPreview: () => resolve.mutate(),
	}
}

const ownRow = (a: ContentAssignmentDTO, replacesCode: string | null, chips: LocaleChipView[]): AssignmentRowView => ({
	id: a.id,
	textId: a.textId,
	code: a.textCode,
	name: a.textName,
	placement: a.placement,
	replacesTextId: a.replacesTextId,
	replacesCode,
	chips,
})

/** Per own text: the in-force chips and the missing-language / expiry warnings. */
function useOwnDetails(own: readonly ContentAssignmentDTO[], now: Date) {
	const types = useContentTypes()
	const texts = useQueries({
		queries: own.map((a) => ({
			queryKey: contentKeys.text(a.textId),
			queryFn: () => contentService.getText(a.textId),
		})),
	})
	const versions = useQueries({
		queries: own.map((a) => ({
			queryKey: contentKeys.versions(a.textId),
			queryFn: () => contentService.versions(a.textId),
		})),
	})
	const chips = own.map((_a, i) => {
		const type = types.data?.find((x) => x.code === texts[i]?.data?.typeCode)
		return inForceChips(type?.requiredLocales ?? [], versions[i]?.data ?? [], now)
	})
	const warnings = textWarnings(
		own.map((a, i) => ({
			code: a.textCode,
			missingLocales: texts[i]?.data?.missingLocales ?? [],
			versions: versions[i]?.data ?? [],
		})),
		now,
	).map((w) => (w.kind === 'expiring' ? { ...w, date: day(w.date) } : w))
	return { chips, warnings }
}

const EMPTY: ContentProductTextsDTO['own'] = []

/**
 * The product editor's "Legal texts" tab (TO-420 content-admin-ui REQ-010):
 * own and inherited assignments, warnings, attach, and the resolve preview.
 */
export function useProductLegalTexts(productId: string): {
	panel: Omit<ProductLegalTextsViewProps, 'own' | 'preview'>
	own: AssignmentListViewProps
	attach: AttachDialogViewProps
	preview: ResolvePreviewViewProps
} {
	const { t, errorMessage } = useContentT()
	const navigate = useNavigate()
	const now = useNow()
	const { caps } = useContentViewer()
	const queryKey = contentKeys.productTexts(productId)
	const view = useQuery({ queryKey, queryFn: () => contentService.productTexts(productId) })
	const own = view.data?.own ?? EMPTY
	const inherited = view.data?.inherited ?? []
	const details = useOwnDetails(own, now)
	const codeOf = (textId: string | null) => [...own, ...inherited].find((a) => a.textId === textId)?.textCode ?? null
	const list = useAssignmentList({
		label: t('assignments.own'),
		source: own,
		queryKey,
		write: (data, ordered: ContentAssignmentDTO[]) => ({ ...(data as ContentProductTextsDTO), own: ordered }),
		product: true,
		inherited,
		rows: own.map((a, i) => ownRow(a, codeOf(a.replacesTextId), details.chips[i] ?? [])),
	})
	const attach = useAttachDialog({ scope: { type: ContentScopeType.PRODUCT, productId }, existing: own, inherited })
	const { open, ...attachProps } = attach
	return {
		panel: {
			t,
			warnings: details.warnings,
			inherited: inherited.map((a) => ({
				id: a.id,
				code: a.textCode,
				name: a.textName,
				replacedByCode: codeOf(a.replacedByTextId),
			})),
			error: view.error ? errorMessage(view.error) : null,
			onAttach: caps.assign ? open : undefined,
			onOpenTenantDefaults: () => void navigate('/content/tenant-defaults'),
		},
		own: list,
		attach: attachProps,
		preview: useResolvePreview(productId),
	}
}
